/**
 * Backend Socket.IO event contracts for automation pipeline.
 *
 * The server emits these events to the client after the user
 * submits onboarding and the automation pipeline runs.
 */

import type { AutomationStage, StageStatus } from '@/types';

// ── Stage names ──────────────────────────────────────────────

export type { AutomationStage };

export type AutomationStageStatus = StageStatus['status'];

// ── Event payloads ───────────────────────────────────────────

/** `automation:progress` — emitted during each automation stage */
export interface AutomationProgressPayload {
  stage: AutomationStage;
  status: AutomationStageStatus;
  /** 0–100 */
  progress: number;
  message?: string;
}

/** `automation:stage-complete` — when a single stage finishes */
export interface AutomationStageCompletePayload {
  stage: AutomationStage;
  /** ISO-8601 date string */
  completedAt: string;
}

/** `automation:complete` — entire pipeline succeeds */
export interface AutomationCompletePayload {
  summary?: {
    strategyId: string;
    postsScraped: number;
    linguisticProfiles: number;
  };
}

/** `automation:error` — pipeline failure */
export interface AutomationErrorPayload {
  stage: AutomationStage;
  error: string;
  recoverable: boolean;
}

// ── Event name constants ─────────────────────────────────────

export const AUTOMATION_EVENTS = {
  PROGRESS: 'automation:progress',
  STAGE_COMPLETE: 'automation:stage-complete',
  COMPLETE: 'automation:complete',
  ERROR: 'automation:error',
} as const;

// ── Event map (useful for generic listeners) ─────────────────

/** For future typed socket consumers */
export interface AutomationEventMap {
  [AUTOMATION_EVENTS.PROGRESS]: AutomationProgressPayload;
  [AUTOMATION_EVENTS.STAGE_COMPLETE]: AutomationStageCompletePayload;
  [AUTOMATION_EVENTS.COMPLETE]: AutomationCompletePayload;
  [AUTOMATION_EVENTS.ERROR]: AutomationErrorPayload;
}

// ── Social / Post event contracts ───────────────────────────

/** `post:published` — webhook confirms post is live */
export interface PostPublishedPayload {
  postId: string;
  platform: string;
  publishedAt: string;
  externalUrl?: string;
}

/** `post:failed` — publish failed after retries or webhook reports failure */
export interface PostFailedPayload {
  postId: string;
  platform: string;
  error: string;
}

/** `post:submitted` — post sent to platform, not yet confirmed */
export interface PostSubmittedPayload {
  postId: string;
  platform: string;
}

/** `post:engagement` — engagement metrics update for a post */
export interface PostEngagementPayload {
  postId: string;
  engagement: {
    likes: number;
    comments: number;
    shares: number;
    impressions: number;
    rate?: number;
  };
}

export const SOCIAL_EVENTS = {
  POST_SUBMITTED: 'post:submitted',
  POST_PUBLISHED: 'post:published',
  POST_FAILED: 'post:failed',
  POST_ENGAGEMENT: 'post:engagement',
} as const;
